import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import LocationOptions, { useAllLocations } from '../components/LocationOptions';
import { T, PageHeader, Section, Field, Row, Alert } from '../components/UI';
import { useAuth } from '../context/AuthContext';
import SignaturePad from '../components/SignaturePad';

const EMPTY = {
  date: new Date(new Date().getTime()+8*60*60*1000).toISOString().split('T')[0],
  time: new Date().toLocaleTimeString('en-AU',{hour:'2-digit',minute:'2-digit',hour12:false}),
  locationType: 'site',
  location: '', transitFrom: '', transitTo: '',
  make: '', model: '', stockNum: '', vinNum: '',
  description: '',
  hasInjuries: false, injuryDetails: '',
  reportedBy: '',
  personInvolved: '',
  witnesses: [],
  licenseSubmitted: false,
  licensePhoto: '',
  signatureAuthorised: '',
  signaturePerson: '',
  thirdParty: { involved: false, name: '', contact: '', insurance: '' },
  photos: [],
};

export default function IncidentForm() {
  const navigate    = useNavigate();
  const { user }    = useAuth();
  const [form, setForm]         = useState(EMPTY);
  const [photos, setPhotos]     = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]       = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const fileRef    = useRef();
  const licenceRef  = useRef();

  const locationGroups = useAllLocations();   // CarSaloon sites + inspection locations + dealerships

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const setTP = (key, val) => setForm(f => ({ ...f, thirdParty: { ...f.thirdParty, [key]: val } }));

  const addWitness = () => setForm(f => ({ ...f, witnesses: [...f.witnesses, { name:'', phone:'', email:'' }] }));
  const updateWitness = (i, key, val) => {
    const w = [...form.witnesses];
    w[i] = { ...w[i], [key]: val };
    setForm(f => ({ ...f, witnesses: w }));
  };
  const removeWitness = (i) => setForm(f => ({ ...f, witnesses: f.witnesses.filter((_,idx)=>idx!==i) }));

  const handlePhotos = (e) => {
    const files = Array.from(e.target.files);
    const previews = files.map(f => ({ file: f, url: URL.createObjectURL(f), name: f.name }));
    setPhotos(p => [...p, ...previews]);
  };

  const removePhoto = (i) => setPhotos(p => p.filter((_,idx)=>idx!==i));

  const validate = () => {
    const errs = {};
    if (!form.date) errs.date = true;
    if (!form.time) errs.time = true;
    if (form.locationType==='site' && !form.location) errs.location = true;
    if (form.locationType==='transit' && !form.transitFrom) errs.transitFrom = true;
    if (form.locationType==='transit' && !form.transitTo) errs.transitTo = true;
    if (!form.description.trim()) errs.description = true;
    if (!form.reportedBy.trim()) errs.reportedBy = true;
    if (!form.personInvolved.trim()) errs.personInvolved = true;
    if (form.hasInjuries && !form.injuryDetails.trim()) errs.injuryDetails = true;
    if (photos.length === 0) errs.photos = true;
    if (!form.licenseSubmitted) errs.licenseSubmitted = true;
    if (!form.signatureAuthorised) errs.signatureAuthorised = true;
    if (!form.signaturePerson) errs.signaturePerson = true;
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) { window.scrollTo({top:0,behavior:'smooth'}); return; }
    setSubmitting(true); setError(null);
    try {
      const formData = new FormData();
      // Append all form fields as JSON (signatures stored as base64 strings)
      const { licensePhoto, ...formData_fields } = form;
      formData.append('data', JSON.stringify({ ...formData_fields, licensePhotoData: licensePhoto }));
      // Append each photo file
      photos.forEach(p => formData.append('photos', p.file));

      await api.post('/incidents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      navigate('/incidents');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  const inlineInput = { ...T.input };

  return (
    <div style={T.page}>
      <PageHeader title="New Incident Report" onBack={()=>navigate('/incidents')}/>
      <form onSubmit={handleSubmit} style={{ maxWidth:'680px', margin:'0 auto', padding:'20px 16px 120px' }}>
        {error && <Alert type="error" style={{ marginBottom:'16px' }}>{error}</Alert>}
        {Object.keys(fieldErrors).length > 0 && (
          <Alert type="error" style={{ marginBottom:'16px' }}>
            <ul style={{ margin:0, paddingLeft:'18px' }}>
              {fieldErrors.date && <li>Date is required</li>}
              {fieldErrors.time && <li>Time is required</li>}
              {fieldErrors.location && <li>Location is required</li>}
              {fieldErrors.transitFrom && <li>Transit from location is required</li>}
              {fieldErrors.transitTo && <li>Transit to location is required</li>}
              {fieldErrors.description && <li>Description is required</li>}
              {fieldErrors.reportedBy && <li>Name of person who reported damage is required</li>}
              {fieldErrors.personInvolved && <li>Name of person who did damage is required</li>}
              {fieldErrors.licenseSubmitted && <li>Driver licence submission confirmation is required</li>}
              {fieldErrors.signatureAuthorised && <li>Authorised person signature is required</li>}
              {fieldErrors.signaturePerson && <li>Person involved signature is required</li>}
              {fieldErrors.injuryDetails && <li>Injury details are required</li>}
              {fieldErrors.photos && <li>At least one photo is required</li>}
            </ul>
          </Alert>
        )}

        {/* Incident Details */}
        <Section title="Incident Details" accent>
          <Row cols={2}>
            <Field label="Date *" error={fieldErrors.date?'Required':null}>
              <input type="date" value={form.date} onChange={e=>set('date',e.target.value)}
                style={{ ...inlineInput, borderColor:fieldErrors.date?'var(--red)':'var(--border-md)' }}/>
            </Field>
            <Field label="Time *" error={fieldErrors.time?'Required':null}>
              <input type="time" value={form.time} onChange={e=>set('time',e.target.value)}
                style={{ ...inlineInput, borderColor:fieldErrors.time?'var(--red)':'var(--border-md)' }}/>
            </Field>
          </Row>

          {/* Location type toggle */}
          <Field label="Incident type *">
            <div style={{ display:'flex', gap:'8px' }}>
              {['site','transit'].map(type=>(
                <button key={type} type="button" onClick={()=>set('locationType',type)}
                  style={{ flex:1, padding:'9px', borderRadius:'var(--radius)', border:`${form.locationType===type?'1.5px solid var(--blue)':'1px solid var(--border-md)'}`, background:form.locationType===type?'var(--blue-light)':'var(--surface)', color:form.locationType===type?'var(--blue)':'var(--muted)', fontSize:'13px', cursor:'pointer', fontFamily:'inherit', fontWeight:form.locationType===type?'500':'400' }}>
                  <i className={`ti ${type==='site'?'ti-building':'ti-car'}`} style={{ marginRight:'6px' }} aria-hidden="true"/>
                  {type==='site'?'At location':'In transit'}
                </button>
              ))}
            </div>
          </Field>

          {form.locationType==='site' && (
            <Field label="Location *" error={fieldErrors.location?'Required':null}>
              <select value={form.location} onChange={e=>{set('location',e.target.value); setFieldErrors(fe=>({...fe,location:false}));}}
                style={{ ...inlineInput, borderColor:fieldErrors.location?'var(--red)':'var(--border-md)' }}>
                <option value="">Select location...</option>
                <LocationOptions groups={locationGroups}/>
              </select>
            </Field>
          )}

          {form.locationType==='transit' && (
            <Row cols={2}>
              <Field label="From *" error={fieldErrors.transitFrom?'Required':null}>
                <select value={form.transitFrom} onChange={e=>{set('transitFrom',e.target.value); setFieldErrors(fe=>({...fe,transitFrom:false}));}}
                  style={{ ...inlineInput, borderColor:fieldErrors.transitFrom?'var(--red)':'var(--border-md)' }}>
                  <option value="">Select location...</option>
                  <LocationOptions groups={locationGroups}/>
                </select>
              </Field>
              <Field label="To *" error={fieldErrors.transitTo?'Required':null}>
                <select value={form.transitTo} onChange={e=>{set('transitTo',e.target.value); setFieldErrors(fe=>({...fe,transitTo:false}));}}
                  style={{ ...inlineInput, borderColor:fieldErrors.transitTo?'var(--red)':'var(--border-md)' }}>
                  <option value="">Select location...</option>
                  <LocationOptions groups={locationGroups}/>
                </select>
              </Field>
            </Row>
          )}

          <Field label="Description *" error={fieldErrors.description?'Required':null}>
            <textarea value={form.description} onChange={e=>{set('description',e.target.value); setFieldErrors(fe=>({...fe,description:false}));}}
              rows={4} style={{ ...inlineInput, resize:'vertical', borderColor:fieldErrors.description?'var(--red)':'var(--border-md)' }}
              placeholder="Describe what happened in detail..."/>
          </Field>
        </Section>

        {/* Vehicle */}
        <Section title="Vehicle (if applicable)" accent>
          <Row cols={2}>
            <Field label="Make">
              <input value={form.make} onChange={e=>set('make',e.target.value)} style={inlineInput} placeholder="e.g. Toyota"/>
            </Field>
            <Field label="Model">
              <input value={form.model} onChange={e=>set('model',e.target.value)} style={inlineInput} placeholder="e.g. Camry"/>
            </Field>
          </Row>
          <Row cols={2}>
            <Field label="Stock #">
              <input value={form.stockNum} onChange={e=>set('stockNum',e.target.value)} style={{ ...inlineInput, fontFamily:'JetBrains Mono,monospace' }} placeholder="Stock number"/>
            </Field>
            <Field label="VIN">
              <input value={form.vinNum} onChange={e=>set('vinNum',e.target.value)} style={{ ...inlineInput, fontFamily:'JetBrains Mono,monospace' }} placeholder="VIN number"/>
            </Field>
          </Row>
        </Section>

        {/* Injuries */}
        <Section title="Injuries" accent>
          <Field label="Were there any injuries?">
            <div style={{ display:'flex', gap:'8px' }}>
              {[{val:false,label:'No injuries',icon:'ti-check',color:'green'},{val:true,label:'Injuries occurred',icon:'ti-alert-circle',color:'red'}].map(opt=>(
                <button key={String(opt.val)} type="button" onClick={()=>set('hasInjuries',opt.val)}
                  style={{ flex:1, padding:'9px', borderRadius:'var(--radius)', border:`${form.hasInjuries===opt.val?`1.5px solid var(--${opt.color})`:'1px solid var(--border-md)'}`, background:form.hasInjuries===opt.val?`var(--${opt.color}-bg)`:'var(--surface)', color:form.hasInjuries===opt.val?`var(--${opt.color})`:'var(--muted)', fontSize:'13px', cursor:'pointer', fontFamily:'inherit' }}>
                  <i className={`ti ${opt.icon}`} style={{ marginRight:'6px' }} aria-hidden="true"/>
                  {opt.label}
                </button>
              ))}
            </div>
          </Field>
          {form.hasInjuries && (
            <Field label="Injury details *" error={fieldErrors.injuryDetails?'Required':null}>
              <textarea value={form.injuryDetails} onChange={e=>{set('injuryDetails',e.target.value); setFieldErrors(fe=>({...fe,injuryDetails:false}));}}
                rows={3} style={{ ...inlineInput, resize:'vertical', borderColor:fieldErrors.injuryDetails?'var(--red)':'var(--border-md)' }}
                placeholder="Describe injuries, who was affected, and whether medical attention was required..."/>
            </Field>
          )}
        </Section>

        {/* People */}
        <Section title="People Involved" accent>
          <Field label="Reported by (who reported the damage) *" error={fieldErrors.reportedBy?'Required':null}>
            <input type="text" value={form.reportedBy} onChange={e=>{set('reportedBy',e.target.value); setFieldErrors(fe=>({...fe,reportedBy:false}));}}
              style={{ ...inlineInput, borderColor:fieldErrors.reportedBy?'var(--red)':'var(--border-md)' }}
              placeholder="Full name of person who reported the damage"/>
          </Field>

          <Field label="Who did the damage *" error={fieldErrors.personInvolved?'Required':null}>
            <input type="text" value={form.personInvolved} onChange={e=>{set('personInvolved',e.target.value); setFieldErrors(fe=>({...fe,personInvolved:false}));}}
              style={{ ...inlineInput, borderColor:fieldErrors.personInvolved?'var(--red)':'var(--border-md)' }}
              placeholder="Full name of person who caused the damage"/>
          </Field>

          <Field label="Witnesses">
            {form.witnesses.map((w,i)=>(
              <div key={i} style={{ background:'var(--bg)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'10px', marginBottom:'8px' }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:'8px', marginBottom:'8px', alignItems:'center' }}>
                  <div style={{ fontSize:'12px', fontWeight:'500', color:'var(--muted)' }}>Witness {i+1}</div>
                  <button type="button" onClick={()=>removeWitness(i)} style={{ background:'transparent', border:'none', cursor:'pointer', color:'var(--red)', padding:'4px', display:'flex', alignItems:'center' }}>
                    <i className="ti ti-x" style={{ fontSize:'14px' }} aria-label="Remove witness"/>
                  </button>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'8px' }}>
                  <input value={w.name||''} onChange={e=>updateWitness(i,'name',e.target.value)} style={inlineInput} placeholder="Full name"/>
                  <input value={w.phone||''} onChange={e=>updateWitness(i,'phone',e.target.value)} style={inlineInput} placeholder="Phone number"/>
                  <input value={w.email||''} onChange={e=>updateWitness(i,'email',e.target.value)} style={inlineInput} placeholder="Email (optional)"/>
                </div>
              </div>
            ))}
            <button type="button" onClick={addWitness}
              style={{ width:'100%', padding:'8px', background:'transparent', border:'1px dashed var(--border-md)', borderRadius:'var(--radius)', color:'var(--blue)', fontSize:'13px', cursor:'pointer', fontFamily:'inherit', marginTop:'4px' }}>
              <i className="ti ti-plus" aria-hidden="true"/> Add witness
            </button>
          </Field>

          {/* Third party */}
          <div style={{ background:'var(--bg)', border:'1px solid var(--border-md)', borderRadius:'var(--radius-lg)', padding:'14px', marginTop:'4px' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: form.thirdParty.involved?'12px':'0' }}>
              <span style={{ fontSize:'13px', fontWeight:'500', color:'var(--text)' }}>Third party involved?</span>
              <label style={{ display:'flex', alignItems:'center', gap:'6px', cursor:'pointer', fontSize:'13px', color:'var(--muted)' }}>
                <input type="checkbox" checked={form.thirdParty.involved} onChange={e=>setTP('involved',e.target.checked)} style={{ width:'16px', height:'16px' }}/>
                Yes
              </label>
            </div>
            {form.thirdParty.involved && (
              <>
                <Row cols={2}>
                  <Field label="Name"><input value={form.thirdParty.name} onChange={e=>setTP('name',e.target.value)} style={inlineInput} placeholder="Full name"/></Field>
                  <Field label="Contact"><input value={form.thirdParty.contact} onChange={e=>setTP('contact',e.target.value)} style={inlineInput} placeholder="Phone or email"/></Field>
                </Row>
                <Field label="Insurance details">
                  <input value={form.thirdParty.insurance} onChange={e=>setTP('insurance',e.target.value)} style={inlineInput} placeholder="Insurance company and policy number"/>
                </Field>
              </>
            )}
          </div>
        </Section>

        {/* Photos */}
        <Section title="Photos *" accent>
          {fieldErrors.photos && <Alert type="error" style={{ marginBottom:'12px' }}>At least one photo is required</Alert>}
          <div onClick={()=>fileRef.current?.click()}
            style={{ border:'1.5px dashed var(--border-md)', borderRadius:'var(--radius)', padding:'24px', textAlign:'center', background:'var(--bg)', cursor:'pointer', borderColor:fieldErrors.photos?'var(--red)':'var(--border-md)' }}>
            <i className="ti ti-camera" style={{ fontSize:'28px', color:'var(--hint)', display:'block', marginBottom:'8px' }} aria-hidden="true"/>
            <div style={{ fontSize:'13px', color:'var(--muted)', marginBottom:'4px' }}>Tap to take photo or upload from gallery</div>
            <div style={{ fontSize:'11px', color:'var(--hint)' }}>At least 1 photo required</div>
          </div>
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={handlePhotos} style={{ display:'none' }}/>

          {photos.length > 0 && (
            <div style={{ display:'flex', flexWrap:'wrap', gap:'8px', marginTop:'12px' }}>
              {photos.map((p,i)=>(
                <div key={i} style={{ position:'relative', width:'72px', height:'72px' }}>
                  <img src={p.url} alt={p.name} style={{ width:'72px', height:'72px', objectFit:'cover', borderRadius:'var(--radius)', border:'1px solid var(--border)' }}/>
                  <button type="button" onClick={()=>removePhoto(i)}
                    style={{ position:'absolute', top:'-6px', right:'-6px', width:'20px', height:'20px', borderRadius:'50%', background:'var(--red)', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <i className="ti ti-x" style={{ fontSize:'11px', color:'#fff' }} aria-label="Remove photo"/>
                  </button>
                </div>
              ))}
              <div onClick={()=>fileRef.current?.click()}
                style={{ width:'72px', height:'72px', border:'1px dashed var(--border-md)', borderRadius:'var(--radius)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
                <i className="ti ti-plus" style={{ fontSize:'20px', color:'var(--hint)' }} aria-hidden="true"/>
              </div>
            </div>
          )}
        </Section>

        {/* Driver Licence */}
        <Section title="Driver Licence" accent>
          {/* Upload area */}
          {!form.licensePhoto ? (
            <div onClick={()=>licenceRef.current?.click()}
              style={{ border:'1.5px dashed var(--border-md)', borderRadius:'var(--radius)', padding:'20px', textAlign:'center', background:'var(--bg)', cursor:'pointer', marginBottom:'12px' }}>
              <i className="ti ti-id" style={{ fontSize:'28px', color:'var(--hint)', display:'block', marginBottom:'8px' }} aria-hidden="true"/>
              <div style={{ fontSize:'13px', color:'var(--muted)', marginBottom:'4px' }}>Upload driver licence photo</div>
              <div style={{ fontSize:'11px', color:'var(--hint)' }}>Take a photo or choose from gallery</div>
            </div>
          ) : (
            <div style={{ position:'relative', marginBottom:'12px' }}>
              <img src={form.licensePhoto} alt="Driver licence" style={{ width:'100%', maxHeight:'160px', objectFit:'cover', borderRadius:'var(--radius)', border:'1px solid var(--green)' }}/>
              <button type="button" onClick={()=>{ set('licensePhoto',''); set('licenseSubmitted',false); }}
                style={{ position:'absolute', top:'6px', right:'6px', background:'rgba(0,0,0,0.6)', border:'none', borderRadius:'var(--radius)', padding:'4px 10px', fontSize:'11px', color:'#fff', cursor:'pointer' }}>
                Remove
              </button>
            </div>
          )}
          <input ref={licenceRef} type="file" accept="image/*" onChange={e=>{
            const file = e.target.files[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = (ev) => { set('licensePhoto', ev.target.result); set('licenseSubmitted', true); setFieldErrors(fe=>({...fe,licenseSubmitted:false})); };
            reader.readAsDataURL(file);
          }} style={{ display:'none' }}/>

          {/* Checkbox — auto-checked when photo uploaded */}
          <div onClick={()=>{ if(!form.licensePhoto){ set('licenseSubmitted',!form.licenseSubmitted); setFieldErrors(fe=>({...fe,licenseSubmitted:false})); }}}
            style={{ display:'flex', alignItems:'center', gap:'12px', padding:'14px', background:form.licenseSubmitted?'var(--green-bg)':'var(--bg)', border:`1.5px solid ${fieldErrors.licenseSubmitted?'var(--red)':form.licenseSubmitted?'var(--green)':'var(--border-md)'}`, borderRadius:'var(--radius)', cursor:form.licensePhoto?'default':'pointer' }}>
            <div style={{ width:'22px', height:'22px', border:`2px solid ${form.licenseSubmitted?'var(--green)':'var(--border-md)'}`, borderRadius:'4px', background:form.licenseSubmitted?'var(--green)':'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              {form.licenseSubmitted && <i className="ti ti-check" style={{ fontSize:'13px', color:'#fff' }} aria-hidden="true"/>}
            </div>
            <div>
              <div style={{ fontSize:'14px', fontWeight:'500', color:form.licenseSubmitted?'var(--green)':'var(--text)' }}>Driver licence has been submitted *</div>
              <div style={{ fontSize:'12px', color:'var(--muted)' }}>Auto-checked when photo is uploaded, or tick manually to confirm</div>
            </div>
          </div>
          {fieldErrors.licenseSubmitted && <div style={{ fontSize:'12px', color:'var(--red)', marginTop:'6px' }}>This confirmation is required before submitting the report</div>}
        </Section>

        {/* Signatures */}
        <Section title="Digital Signatures" accent>
          <div style={{ marginBottom:'20px' }}>
            <SignaturePad
              label="Car Saloon authorised signatory *"
              value={form.signatureAuthorised}
              onChange={val=>{ set('signatureAuthorised',val); setFieldErrors(fe=>({...fe,signatureAuthorised:false})); }}
            />
            {fieldErrors.signatureAuthorised && <div style={{ fontSize:'12px', color:'var(--red)', marginTop:'4px' }}>Authorised person signature is required</div>}
          </div>
          <div>
            <SignaturePad
              label="Person involved signature *"
              value={form.signaturePerson}
              onChange={val=>{ set('signaturePerson',val); setFieldErrors(fe=>({...fe,signaturePerson:false})); }}
            />
            {fieldErrors.signaturePerson && <div style={{ fontSize:'12px', color:'var(--red)', marginTop:'4px' }}>Person involved signature is required</div>}
          </div>
        </Section>

        {/* Submit */}
        <div style={{ display:'flex', gap:'8px', paddingTop:'8px' }}>
          <button type="button" onClick={()=>navigate('/incidents')} style={{ ...T.btnSecondary, flex:1 }}>Cancel</button>
          <button type="submit" disabled={submitting} style={{ ...T.btnPrimary, flex:2, justifyContent:'center', opacity:submitting?0.6:1 }}>
            <i className="ti ti-send" aria-hidden="true"/> {submitting?'Submitting...':'Submit Report'}
          </button>
        </div>
      </form>
    </div>
  );
}
